import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { Company } from '../../../../shared/dto/company';
import { SelectItem } from 'primeng/api';
import { CompanyUser } from '../../../../shared/dto/customer-management/company-user';
import { NavigationStart, Router } from '@angular/router';
import { CustomerManagementService } from '../../../../data-services/customer-management.service';
import { OrganizationManagementService } from '../../../services/organization-management.service';
import { CompanyLite } from '../../../../shared/dto/company-lite';
import PreferencesUtil from '../../../company-profile/preferences-util';
import { isNullOrUndefined } from '../../../../shared/utils/is-null-or-undefined';
import { ModalComponent } from '../../../../controls/modal/modal.component';
import { UserService } from '../../../../shared/services/user.service';
import { NgForm } from '@angular/forms';
import { touchForm } from '../../../../shared/touch-form';
import { CompanyInstructionService } from '../../../../data-services/company-instruction.service';
import { CompanyInstruction, CompanyInstructionType } from '../../../../shared/dto/company-instruction';

// Service types that require a client user selection
export enum ServiceType {
  PATENT_FORECAST = 'Patent Forecast',
  PORTFOLIO_FORECAST = 'Portfolio Forecast',
  PROJECT = 'Project',
  ESTIMATE = 'Estimate'
}

@Component({
  selector: 'townip-new-client-selector',
  templateUrl: './new-client-selector.component.html',
  styleUrls: ['./new-client-selector.component.scss']
})
export class NewClientSelectorComponent implements OnInit {
  @Input()
  public clientCompanies: CompanyLite[];

  @ViewChild(ModalComponent, { static: true })
  public modal: ModalComponent;

  @ViewChild(NgForm, { static: true })
  public form: NgForm;

  public companies: CompanyLite[];
  public selectedCompanyLite: CompanyLite;
  public selectedCompany: Company;
  public companyUsers: SelectItem[];
  public selectedCompanyUser: CompanyUser;
  public serviceType: ServiceType;

  public preIntakeInstruction: CompanyInstruction;

  private modalSubscription: Subscription;

  constructor(
    private router: Router,
    private customerManagementService: CustomerManagementService,
    private orgService: OrganizationManagementService,
    private userService: UserService,
    private companyInstructionService: CompanyInstructionService,
  ) {
    this.companies = [];
    this.companyUsers = [];
  }

  public ngOnInit(): void {
  }

  private clear(): void {
    this.selectedCompany = null;
    this.selectedCompanyLite = null;
    this.selectedCompanyUser = null;
    this.companies = null;
    this.companyUsers = [];
    this.preIntakeInstruction = null;
  }

  /**
   * Show this modal for either a Project, Estimate, or Forecaster.
   * @param {string} serviceType
   */
  public async show(serviceType: ServiceType): Promise<void> {
    this.clear();
    this.serviceType = serviceType;
    if (serviceType === ServiceType.PATENT_FORECAST || serviceType === ServiceType.PORTFOLIO_FORECAST) {
      this.companies = await this.customerManagementService
        .getCompanyLitesByUserIdAndCompanyService(this.userService.getUser().id, 'FORECASTER')
        .toPromise();
    } else {
      this.companies = await this.customerManagementService
        .getCompanyLitesByUserIdAndCompanyService(this.userService.getUser().id, 'PORTAL')
        .toPromise();
    }
    this.setupCompanies();
    this.modalSubscription = this.router
      .events
      .subscribe((event) => {
        const isOpen = this.modal && this.modal.modal.isShown;
        // TODO: Angular Upgrade, need to look at what this did originally.
        // const redirectToDashboard = event.url ? event.url.startsWith('/dashboard') : false;
        const redirectToDashboard = false;
        if (event instanceof NavigationStart && isOpen && !redirectToDashboard) {
          this.hide();
        }
      });
  }

  private setupCompanies(): void {
    this.companies.sort((a, b) => {
      const companyNameA = a.companyName.toLocaleLowerCase();
      const companyNameB = b.companyName.toLocaleLowerCase();
      if (companyNameA > companyNameB) {
        return 1;
      }
      if (companyNameA < companyNameB) {
        return -1;
      }
      return 0;
    });
    this.modal.show();
  }

  public hide(): void {
    if (this.form) {
      this.form.resetForm();
    }
    this.modal.hide();
    this.clear();
    this.modalSubscription.unsubscribe();
  }

  public selectCompany(): void {
    if (this.selectedCompanyLite === null) {
      return;
    }
    this.selectedCompanyUser = null;
    this.customerManagementService
      .getCompanyById(this.selectedCompanyLite.id)
      .then((retrievedCompany) => {
        this.selectedCompany = retrievedCompany;
        this.companyUsers = [];
        this.selectedCompany.companyUsers.forEach(companyUser => {
          const restrictedUser = companyUser.user.authorities.indexOf('RESTRICTED_COMPANY_USER') >= 0;
          if (restrictedUser && this.serviceType !== ServiceType.PROJECT) {
            return;
          }

          if (companyUser.user.active) {
            this.companyUsers
              .push({ label: companyUser.user.firstName + ' ' + companyUser.user.lastName, value: companyUser });
          }
        });

        this.companyUsers.sort((a, b) => {
          const userNameA = a.label.toLocaleLowerCase();
          const userNameB = b.label.toLocaleLowerCase();
          if (userNameA > userNameB) {
            return 1;
          }
          if (userNameA < userNameB) {
            return -1;
          }
          return 0;
        });

        if (this.companyUsers.length === 1) {
          this.selectedCompanyUser = this.companyUsers[0].value;
          this.selectCompanyUser();
        } else {
          this.companyUsers.unshift({ label: 'Select User', value: null });
        }
      });

    // Pre-Intake Client Care Instruction applies to Estimate/Project intake,
    // not to forecast flows (bug 44959).
    if (this.serviceType === ServiceType.PATENT_FORECAST || this.serviceType === ServiceType.PORTFOLIO_FORECAST) {
      return;
    }

    this.companyInstructionService
      .getInstructionByType(this.selectedCompanyLite.id, CompanyInstructionType.ClientCarePreIntake)
      .subscribe((instruction) => this.preIntakeInstruction = instruction);
  }

  public selectCompanyUser(): void {
    if (isNullOrUndefined(this.selectedCompanyUser)) {
      // Do Nothing
      return;
    }

    PreferencesUtil.copyCompanyPreferencesToUser(this.selectedCompany, this.selectedCompanyUser);
  }

  public submit(): void {
    touchForm(this.form);

    if (this.form.invalid) {
      return;
    }

    this.orgService.setCompany(this.selectedCompany);
    this.orgService.setCompanyUser(this.selectedCompanyUser);
    if (this.serviceType === ServiceType.PROJECT) {
      // OLD estimate creation page:
      // this.router.navigate(['/estimate/new-project/patent']);
      // NEW estimate creation page:
      this.router.navigate(['/estimate/new-estimate-2'], { queryParams: { project: true } });
      // Forecast pages:
    } else if (this.serviceType === ServiceType.PATENT_FORECAST) {
      this.router.navigate(['/forecast/new']);
    } else if (this.serviceType === ServiceType.PORTFOLIO_FORECAST) {
      this.router.navigate(['/forecast/create-portfolio-forecast']);
    } else {
      // OLD estimate creation page:
      // this.router.navigate(['/estimate/new-estimate/patent']);
      // NEW estimate creation page:
      this.router.navigate(['/estimate/new-estimate-2']);
    }
    this.hide();
  }
}
